// The fake api's organisation Venues screen (spec/13 §13, F-ORG-01; ADR-0019 Phase 2, F-ORG-17), past
// the console gate in `fake-server.ts`:
//
//   GET  /v1/organizations/{o}/venues                     listVenues            venues:read, archived left out
//   POST /v1/organizations/{o}/venues                     createVenue           venues:create, `setup`, slug unique
//   GET  /v1/organizations/{o}/venues/{v}                 getVenue              for a venue made here (ETag "venue-1")
//   POST /v1/organizations/{o}/venues/{v}/archive         archiveVenue          venues:archive, If-Match
//   GET  /v1/organizations/{o}/franchisees                listFranchisees       franchise:read
//   GET  /v1/organizations/{o}/franchisees/{f}/venues     listFranchiseeVenues  franchise:read
//
// The seeded venues are `fake-server.ts`'s own (its `getVenue` answers them with their ETags, which
// `etagOf` reads back here), so an archive checks `If-Match` against what the console really read.

import type { Franchisee, Venue } from '@clavian/api-client';
import { CreateVenueSchema } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

interface OrgVenuesHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string) => FakeResponse;
  readonly can: (permission: string) => boolean;
  readonly organizationId: string;
  /** The organisation's venues `fake-server.ts` serves, read fresh on each call. */
  readonly seeded: () => readonly Venue[];
  /** The ETag `fake-server.ts`'s `getVenue` answers for a seeded venue. */
  readonly etagOf: (venueId: string) => string | undefined;
  /** Seed the organisation as a franchisor with one franchisee and its venue. */
  readonly franchisor?: boolean;
}

const NOW = '2026-09-22T09:00:00.000Z';

export const FRANCHISEE = {
  id: 'org_01k5znw4hmfay8vvr14d2pf9a1',
  name: 'Playground Pune (franchise)',
  slug: 'playground-pune',
} as const;

export function createFakeOrgVenues(helpers: OrgVenuesHelpers) {
  const { json, problem, can, organizationId } = helpers;
  const state = {
    created: [] as Venue[],
    archived: new Set<string>(),
    franchisees: (helpers.franchisor === true
      ? [{ ...FRANCHISEE, kind: 'franchisee', status: 'active', franchisorOrganizationId: organizationId, createdAt: '2026-05-01T09:00:00.000Z' }]
      : []) as Franchisee[],
    serial: 0,
  };

  const page = (rows: readonly unknown[]) => json(200, { data: rows, page: { cursor: null, hasMore: false, count: rows.length } });
  const forbidden = () => problem(403, 'forbidden', 'Your role does not allow this.');

  const all = (): Venue[] => [...helpers.seeded(), ...state.created];
  const live = (): Venue[] => all().filter((venue) => !state.archived.has(venue.id));

  function franchiseeVenue(): Venue {
    const base = helpers.seeded()[0] as Venue;
    return {
      ...base,
      id: 'ven_01k5znw4hmfay8vvr14d2pf9a2',
      organizationId: FRANCHISEE.id,
      name: 'Playground Koregaon Park',
      slug: 'playground-koregaon-park',
      address: { line1: '7 North Main Road', locality: 'Pune', region: 'Maharashtra', postalCode: '411001', country: 'IN' },
    };
  }

  function create(input: Record<string, unknown>): FakeResponse {
    if (!can('venues:create')) return forbidden();
    const parsed = CreateVenueSchema.safeParse(input);
    if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
    if (all().some((venue) => venue.slug === parsed.data.slug)) return problem(409, 'slug_taken', 'Another venue already has that slug.');
    if (parsed.data.currency !== undefined && parsed.data.currency !== 'INR') return problem(422, 'currency_mismatch', "The currency is not the organisation's.");
    state.serial += 1;
    const base = helpers.seeded()[0] as Venue;
    const venue: Venue = {
      ...base,
      id: `ven_01k5znw4hmfay8vvr14d2pf9b${state.serial}`,
      name: parsed.data.name,
      slug: parsed.data.slug,
      timezone: parsed.data.timezone,
      businessDayCutoff: parsed.data.businessDayCutoff ?? '06:00',
      businessDayCutoffHistory: [],
      currency: 'INR',
      address: parsed.data.address ?? null,
      contact: parsed.data.contact ?? {},
      openingHours: { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] },
      amenities: [],
      photos: { cover: null, gallery: [] },
      status: 'setup',
      publicListing: parsed.data.publicListing ?? false,
      featured: false,
      partner: false,
      sponsored: false,
      createdAt: NOW,
      updatedAt: NOW,
      archivedAt: null,
    };
    state.created.push(venue);
    return json(201, venue, { etag: '"venue-1"' });
  }

  /** `undefined` for a request that is not one of these routes. */
  function handle(method: string, pathname: string, headers: Readonly<Record<string, string>>, input: Record<string, unknown>): FakeResponse | undefined {
    const organizationPath = `/v1/organizations/${organizationId}`;
    if (!pathname.startsWith(organizationPath)) return undefined;
    const rest = pathname.slice(organizationPath.length);

    if (rest === '/venues') {
      if (method === 'GET') return can('venues:read') ? page(live()) : forbidden();
      if (method === 'POST') return create(input);
      return undefined;
    }
    const one = /^\/venues\/([^/]+)$/.exec(rest);
    if (one !== null && method === 'GET') {
      const made = state.created.find((venue) => venue.id === one[1]);
      return made === undefined ? undefined : json(200, made, { etag: '"venue-1"' });
    }
    const archive = /^\/venues\/([^/]+)\/archive$/.exec(rest);
    if (archive !== null && method === 'POST') {
      if (!can('venues:archive')) return forbidden();
      const venue = all().find((candidate) => candidate.id === archive[1]);
      if (venue === undefined) return problem(404, 'not_found', 'No such venue in this organisation.');
      if (state.archived.has(venue.id)) return problem(409, 'conflict', 'The venue is already archived.');
      const ifMatch = headers['if-match'];
      if (ifMatch === undefined) return problem(400, 'validation_failed', 'If-Match required');
      if (ifMatch !== (helpers.etagOf(venue.id) ?? '"venue-1"')) return problem(412, 'stale_write', 'Someone else changed the venue since you read it.');
      state.archived.add(venue.id);
      return json(200, { ...venue, status: 'closed', publicListing: false, archivedAt: NOW }, { etag: '"venue-archived"' });
    }

    if (rest === '/franchisees' && method === 'GET') return can('franchise:read') ? page(state.franchisees) : forbidden();
    const franchiseeVenues = /^\/franchisees\/([^/]+)\/venues$/.exec(rest);
    if (franchiseeVenues !== null && method === 'GET') {
      if (!can('franchise:read')) return forbidden();
      if (!state.franchisees.some((franchisee) => franchisee.id === franchiseeVenues[1])) return problem(404, 'not_found', 'No such franchisee of this organisation.');
      return page([franchiseeVenue()]);
    }
    return undefined;
  }

  return { state, handle };
}

export type FakeOrgVenues = ReturnType<typeof createFakeOrgVenues>;
